/**
 * CreateCourierForm — RHF + Zod form for the "Novo Entregador" tab.
 *
 * Wraps the legacy Card 1 flow:
 *   - personal data (name, CPF, e-mail, password, phone)
 *   - vehicle type (radio with three icons)
 *   - documents (face + identity uploads via `<ImageUpload>`)
 *   - approval flags (approveDocuments, autoVerify)
 *
 * On submit calls `useCreateCourierRegistration` (POST /api/v1/admin/couriers
 * with the extended schema) and resets the form on success. The bucket
 * `users-documents` is the same one the legacy panel used.
 *
 * Rendered by BOTH the staff panel (`NovoEntregadorPage`, `.merchant-theme`
 * scoped at the app root) and the admin panel (`RegistrationsPage`, global
 * dark theme, no `.merchant-theme` ancestor). To look native in staff and
 * still resolve correctly in admin, the form self-scopes `.merchant-theme`
 * on its own root (same technique `RejectModal` uses for its portal) instead
 * of relying on the old `bg-bg-card`/`border-border`/`text-primary` admin
 * dark-theme utilities, which used to render as an off-brand slate box with
 * an orange legend when this form was mounted inside staff's light panel.
 * Section cards/labels/inputs mirror the staff wizard (`ComercioFields`,
 * `MerchantDetailPage`) token-for-token. Admin's bundle now also imports
 * `merchant-theme.css` (see `apps/admin/src/main.tsx`) so the CSS vars this
 * form relies on are defined there too.
 */
import {
  forwardRef,
  useMemo,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Bike, Car as CarIcon, Eye, EyeOff, Zap } from 'lucide-react';
import { Button, Card } from '../components/merchant';
import { ImageUpload } from '../components/domain/ImageUpload';
import type { components } from '../api/generated/api';
import { isApiError } from '../api/errors';
import { toast } from '../lib/toast';
import { isValidCpf, maskCpf, maskPhone, onlyDigits } from '../lib/cpf';
import { passwordSchema } from '../lib/password';
import { PIX_KEY_TYPE_VALUES, isValidPixKey, type PixKeyType } from '../lib/pix';
import type { StaffCreateCourierPayload } from './types';
import { useCreateCourierRegistration, type CreateCourierPayload } from './hooks';

type VehicleType = components['schemas']['VehicleTypeEnum'];
type CourierResponse = components['schemas']['CourierResponse'];

/** PT-BR labels for the PIX-key-type select (mirrors the wizard's ComercioFields). */
const PIX_TYPE_LABEL: Record<PixKeyType, string> = {
  CPF: 'CPF',
  CNPJ: 'CNPJ',
  EMAIL: 'E-mail',
  PHONE: 'Telefone',
  RANDOM: 'Chave aleatória',
};

const VEHICLE_OPTIONS: ReadonlyArray<{ value: VehicleType; label: string }> = [
  { value: 'BIKE', label: 'Bicicleta' },
  { value: 'MOTORCYCLE', label: 'Moto' },
  { value: 'CAR', label: 'Carro' },
];

const VEHICLE_ICONS: Record<VehicleType, ReactElement> = {
  BIKE: <Bike size={16} aria-hidden />,
  MOTORCYCLE: <Zap size={16} aria-hidden />,
  CAR: <CarIcon size={16} aria-hidden />,
};

/* ------------------------------------------------------------------ */
/* Field primitives — mirror `apps/staff/src/wizard/fields.tsx` token-  */
/* for-token so this shared form matches the staff wizard exactly.     */
/* Kept local (not imported) because that file is app-scoped and this  */
/* component also ships to admin.                                      */
/* ------------------------------------------------------------------ */

const SECTION_TITLE_CLASS = 'm-0 mb-4 text-[17px] font-bold text-[var(--m-fg)]';
const FIELD_LABEL_CLASS = 'mb-1.5 block text-[12px] font-bold uppercase tracking-[0.04em] text-[var(--m-fg-muted)]';

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }): ReactElement {
  return (
    <label htmlFor={htmlFor} className={FIELD_LABEL_CLASS}>
      {children}
    </label>
  );
}

function FieldError({ message }: { message?: string | undefined }): ReactElement | null {
  if (!message) return null;
  return <p className="mt-1 text-[12px] font-medium text-[var(--m-danger)]">{message}</p>;
}

function FieldHint({ children }: { children: ReactNode }): ReactElement {
  return <p className="mt-1.5 text-[12px] text-[var(--m-fg-subtle)]">{children}</p>;
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  id: string;
  label: string;
  error?: string | undefined;
  helperText?: string | undefined;
  suffix?: ReactNode;
  /** Ocupa as 2 colunas do grid. O primitivo gêmeo do wizard staff (wizard/fields.tsx) tem isto;
   *  esta cópia tinha perdido, e por isso "Nome completo" ficava em meia coluna. */
  full?: boolean;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id, label, error, helperText, suffix, full, ...input },
  ref,
): ReactElement {
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div
        className={
          'flex h-11 w-full items-center gap-2 rounded-[var(--m-r-md)] border bg-[var(--m-surface)] px-3.5 ' +
          'transition-colors focus-within:border-[var(--m-brand)] focus-within:ring-2 focus-within:ring-[var(--m-smoothie)] ' +
          (error ? 'border-[var(--m-danger)]' : 'border-[var(--m-border)]')
        }
      >
        <input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          className="w-full bg-transparent text-[14px] text-[var(--m-fg)] outline-none focus-visible:ring-0 placeholder:text-[var(--m-fg-subtle)]"
          {...input}
        />
        {suffix ? <span className="shrink-0 text-[var(--m-fg-subtle)]">{suffix}</span> : null}
      </div>
      {error ? <FieldError message={error} /> : helperText ? <FieldHint>{helperText}</FieldHint> : null}
    </div>
  );
});

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'> {
  id: string;
  label: string;
  error?: string | undefined;
  children: ReactNode;
}

const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { id, label, error, children, ...select },
  ref,
): ReactElement {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <select
        ref={ref}
        id={id}
        aria-invalid={error ? true : undefined}
        className={
          'h-11 w-full rounded-[var(--m-r-md)] border bg-[var(--m-surface)] px-3.5 text-[14px] text-[var(--m-fg)] ' +
          'outline-none transition-colors focus:border-[var(--m-brand)] focus:ring-2 focus:ring-[var(--m-smoothie)] ' +
          (error ? 'border-[var(--m-danger)]' : 'border-[var(--m-border)]')
        }
        {...select}
      >
        {children}
      </select>
      <FieldError message={error} />
    </div>
  );
});

/**
 * Build the Zod schema. The PIX fields are always present (as optionals) so the
 * form type is stable, but their REQUIREDness is applied via `superRefine` only
 * when `showPix` is active — keeping the admin usage (no `showPix`) identical.
 */
function buildSchema(showPix: boolean) {
  return z
    .object({
      fullName: z.string().min(1, 'Nome obrigatório'),
      cpf: z.string().refine(isValidCpf, 'CPF inválido'),
      email: z.string().email('E-mail inválido'),
      password: passwordSchema,
      phone: z.string().optional(),
      vehicleType: z.enum(['BIKE', 'MOTORCYCLE', 'CAR']),
      facePhotoUrl: z.string().min(1, 'Foto de rosto obrigatória'),
      identityPhotoUrl: z.string().min(1, 'Foto do documento obrigatória'),
      approveDocuments: z.boolean(),
      autoVerify: z.boolean(),
      pixKeyType: z.enum(PIX_KEY_TYPE_VALUES).optional(),
      pixKey: z.string().optional(),
    })
    .superRefine((v, ctx) => {
      if (!showPix) return;
      if (!v.pixKeyType) {
        ctx.addIssue({ path: ['pixKeyType'], code: z.ZodIssueCode.custom, message: 'Selecione o tipo de chave' });
      }
      if (!v.pixKey?.trim()) {
        ctx.addIssue({ path: ['pixKey'], code: z.ZodIssueCode.custom, message: 'Chave PIX obrigatória' });
      } else if (v.pixKeyType && !isValidPixKey(v.pixKeyType, v.pixKey)) {
        ctx.addIssue({ path: ['pixKey'], code: z.ZodIssueCode.custom, message: 'Chave PIX inválida para o tipo' });
      }
    });
}

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: FormValues = {
  fullName: '',
  cpf: '',
  email: '',
  password: '',
  phone: '',
  vehicleType: 'MOTORCYCLE',
  facePhotoUrl: '',
  identityPhotoUrl: '',
  approveDocuments: true,
  autoVerify: true,
  pixKeyType: undefined,
  pixKey: '',
};

export interface CreateCourierFormProps {
  onSuccess?: (courier: CourierResponse) => void;
  /** Render the PIX payout fields and require them (staff flow, EP-3). */
  showPix?: boolean;
  /**
   * When provided (staff flow), the submit posts via this callback (the staff
   * endpoint hook) instead of the admin `useCreateCourierRegistration`.
   */
  onSubmitOverride?: (payload: StaffCreateCourierPayload) => Promise<CourierResponse>;
}

export function CreateCourierForm({
  onSuccess,
  showPix = false,
  onSubmitOverride,
}: CreateCourierFormProps): ReactElement {
  const mutation = useCreateCourierRegistration();
  const [showPassword, setShowPassword] = useState(false);

  const schema = useMemo(() => buildSchema(showPix), [showPix]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const phoneDigits = onlyDigits(values.phone ?? '');
    const base: CreateCourierPayload = {
      email: values.email,
      password: values.password,
      fullName: values.fullName,
      cpf: onlyDigits(values.cpf),
      vehicleType: values.vehicleType,
      facePhotoUrl: values.facePhotoUrl,
      identityPhotoUrl: values.identityPhotoUrl,
      approveDocuments: values.approveDocuments,
      autoVerify: values.autoVerify,
      ...(phoneDigits ? { phone: phoneDigits } : {}),
    };
    try {
      let result: CourierResponse;
      if (onSubmitOverride) {
        // Staff flow: the schema guarantees pixKeyType/pixKey are set when showPix.
        const staffPayload: StaffCreateCourierPayload = {
          ...base,
          pixKey: (values.pixKey ?? '').trim(),
          pixKeyType: values.pixKeyType as PixKeyType,
        };
        result = await onSubmitOverride(staffPayload);
      } else {
        result = await mutation.mutateAsync(base);
      }
      toast.success('Entregador cadastrado');
      form.reset(DEFAULTS);
      onSuccess?.(result);
    } catch (err) {
      if (isApiError(err)) toast.fromApiError(err);
      else toast.error('Falha ao cadastrar entregador');
    }
  });

  const facePhotoUrl = form.watch('facePhotoUrl');
  const identityPhotoUrl = form.watch('identityPhotoUrl');
  const vehicleType = form.watch('vehicleType');
  const approveDocuments = form.watch('approveDocuments');
  const autoVerify = form.watch('autoVerify');
  const cpfValue = form.watch('cpf');
  const phoneValue = form.watch('phone');
  const pixKeyType = form.watch('pixKeyType');
  const pixKey = form.watch('pixKey');

  // O fluxo staff submete por `onSubmitOverride`, não pela mutation admin de :237 — sem o
  // `isSubmitting` o botão nunca desabilitava lá e o duplo clique virava 2 POSTs (o 2º = 409).
  const isPending = mutation.isPending || form.formState.isSubmitting;

  return (
    <form onSubmit={onSubmit} className="merchant-theme grid gap-4">
      <Card>
        <h3 className={SECTION_TITLE_CLASS}>Dados pessoais</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            id="courier-fullName"
            label="Nome completo"
            full
            placeholder="Ex.: Pedro Santos"
            error={form.formState.errors.fullName?.message}
            {...form.register('fullName')}
          />
          <TextField
            id="courier-cpf"
            label="CPF"
            placeholder="000.000.000-00"
            maxLength={14}
            value={maskCpf(cpfValue ?? '')}
            onChange={(e) => form.setValue('cpf', maskCpf(e.target.value), { shouldValidate: true })}
            error={form.formState.errors.cpf?.message}
          />
          <TextField
            id="courier-email"
            label="E-mail"
            type="email"
            placeholder="email@exemplo.com"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <TextField
            id="courier-password"
            label="Senha inicial"
            type={showPassword ? 'text' : 'password'}
            helperText="12+ caracteres com maiúscula, minúscula, número e símbolo."
            error={form.formState.errors.password?.message}
            suffix={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="text-[var(--m-fg-subtle)] hover:text-[var(--m-fg)]"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            }
            {...form.register('password')}
          />
          <TextField
            id="courier-phone"
            label="Telefone"
            placeholder="(21) 99999-9999"
            maxLength={15}
            value={maskPhone(phoneValue ?? '')}
            onChange={(e) => form.setValue('phone', maskPhone(e.target.value), { shouldValidate: false })}
            error={form.formState.errors.phone?.message}
          />
        </div>
      </Card>

      <Card>
        <h3 className={SECTION_TITLE_CLASS}>Veículo</h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Veículo">
          {VEHICLE_OPTIONS.map((option) => {
            const id = `courier-vehicle-${option.value}`;
            const selected = vehicleType === option.value;
            return (
              <label
                key={option.value}
                htmlFor={id}
                className={
                  'flex cursor-pointer items-center gap-2 rounded-[var(--m-r-md)] border px-3 py-2.5 transition-colors ' +
                  (selected
                    ? 'border-[var(--m-brand)] bg-[var(--m-bg-alt)] ring-1 ring-[var(--m-brand)]'
                    : 'border-[var(--m-border)] bg-[var(--m-surface)] hover:border-[var(--m-border-strong)]')
                }
              >
                <input
                  type="radio"
                  id={id}
                  name="courier-vehicle"
                  className="sr-only"
                  checked={selected}
                  onChange={() => form.setValue('vehicleType', option.value, { shouldValidate: true })}
                />
                <span className={selected ? 'text-[var(--m-brand)]' : 'text-[var(--m-fg-subtle)]'}>
                  {VEHICLE_ICONS[option.value]}
                </span>
                <span className={`text-[14px] font-semibold text-[var(--m-fg)]`}>{option.label}</span>
              </label>
            );
          })}
        </div>
      </Card>

      <Card>
        <h3 className={SECTION_TITLE_CLASS}>Documentos</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className={FIELD_LABEL_CLASS}>Foto de rosto</span>
            <ImageUpload
              bucket="users-documents"
              value={facePhotoUrl}
              onChange={(url) => form.setValue('facePhotoUrl', url ?? '', { shouldValidate: true })}
              aspectRatio="4:3"
            />
            {form.formState.errors.facePhotoUrl ? (
              <FieldError message={form.formState.errors.facePhotoUrl.message} />
            ) : (
              <FieldHint>Rosto visível, sem óculos escuros ou boné.</FieldHint>
            )}
          </div>
          <div>
            <span className={FIELD_LABEL_CLASS}>Foto do documento</span>
            <ImageUpload
              bucket="users-documents"
              value={identityPhotoUrl}
              onChange={(url) => form.setValue('identityPhotoUrl', url ?? '', { shouldValidate: true })}
              aspectRatio="4:3"
            />
            {form.formState.errors.identityPhotoUrl ? (
              <FieldError message={form.formState.errors.identityPhotoUrl.message} />
            ) : (
              <FieldHint>RG ou CNH, frente legível.</FieldHint>
            )}
          </div>
        </div>
      </Card>

      {showPix ? (
        <Card>
          <h3 className={SECTION_TITLE_CLASS}>Recebimento (PIX)</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              id="courier-pix-type"
              label="Tipo de chave PIX"
              value={pixKeyType ?? ''}
              onChange={(e) =>
                form.setValue('pixKeyType', (e.target.value || undefined) as PixKeyType | undefined, {
                  shouldValidate: true,
                })
              }
              error={form.formState.errors.pixKeyType?.message}
            >
              <option value="">Selecione o tipo</option>
              {PIX_KEY_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {PIX_TYPE_LABEL[t]}
                </option>
              ))}
            </SelectField>
            <TextField
              id="courier-pix-key"
              label="Chave PIX"
              placeholder="Chave de recebimento do entregador"
              maxLength={140}
              value={pixKey ?? ''}
              onChange={(e) => form.setValue('pixKey', e.target.value, { shouldValidate: true })}
              error={form.formState.errors.pixKey?.message}
            />
          </div>
        </Card>
      ) : null}

      {/* Estrutura (Card + checkbox nativo + aviso de bloqueio) vem de development;
          os rótulos são o STA-13: o operador do back-office não fala `documentStatus`
          nem `isVerified`, então o nome do campo do banco saiu do texto visível. */}
      <Card>
        <h3 className={SECTION_TITLE_CLASS}>Aprovação</h3>
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-[13px] font-medium text-[var(--m-fg)]" htmlFor="courier-approve-docs">
            <input
              type="checkbox"
              id="courier-approve-docs"
              checked={approveDocuments}
              onChange={(e) => form.setValue('approveDocuments', e.target.checked)}
              className="h-4 w-4 accent-[var(--m-brand)]"
            />
            Aprovar os documentos agora, sem passar pela fila de análise
          </label>
          {!approveDocuments ? (
            <p
              data-testid="courier-approve-docs-warning"
              className="text-[12px] font-medium text-[var(--m-danger)]"
            >
              Sem aprovar, o entregador fica <strong>bloqueado no app</strong> (tela “Sua conta está
              em análise”) até um admin aprovar os documentos em Cadastros.
            </p>
          ) : null}
          <label className="flex items-center gap-2 text-[13px] font-medium text-[var(--m-fg)]" htmlFor="courier-auto-verify">
            <input
              type="checkbox"
              id="courier-auto-verify"
              checked={autoVerify}
              onChange={(e) => form.setValue('autoVerify', e.target.checked)}
              className="h-4 w-4 accent-[var(--m-brand)]"
            />
            Marcar o entregador como verificado
          </label>
        </div>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" onClick={() => form.reset(DEFAULTS)} disabled={isPending}>
          Limpar
        </Button>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Cadastrando…' : 'Cadastrar entregador'}
        </Button>
      </div>
    </form>
  );
}
